var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { SignalWatcher } from "@lit-labs/preact-signals";
import { draft } from "../state";
// Stable, deterministic starting order so an ordering question never opens pre-solved,
// yet is identical on every render and reload for a given task.
function seededOrder(tid, options) {
    const hash = (s) => {
        let h = 2166136261;
        for (let i = 0; i < s.length; i++) {
            h ^= s.charCodeAt(i);
            h = Math.imul(h, 16777619);
        }
        return h >>> 0;
    };
    return [...options].sort((a, b) => hash(tid + a) - hash(tid + b));
}
let ModeNavigate = class ModeNavigate extends SignalWatcher(LitElement) {
    constructor() {
        super(...arguments);
        this.tasks = [];
    }
    static { this.styles = css `
    :host { display: block; padding: 12px; }
    .task { background: var(--color-white); padding: 12px; border-radius: var(--radius-md);
            margin: 8px 0; box-shadow: var(--shadow-sm); }
    .qnum { font-size: 12px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
            color: var(--color-blue-600, #2563eb); margin-bottom: 4px; }
    .prompt { font-weight: 600; margin-bottom: 8px; }
    textarea, select { width: 100%; padding: 8px; border: 1px solid var(--color-slate-300);
                       border-radius: var(--radius-sm); font-family: var(--font-base); }
    label.opt { display: block; padding: 4px 0; }
    .order-hint { color: var(--color-slate-500); font-size: 13px; margin: 0 0 6px; }
    .order-row { display: grid; grid-template-columns: 26px 1fr auto; gap: 10px; align-items: center;
                 padding: 8px 10px; background: var(--color-white); border: 1px solid var(--color-slate-200);
                 border-radius: var(--radius-md); margin-bottom: 6px; }
    .order-row .seqno { font-weight: 700; color: var(--color-slate-400); font-size: 13px; text-align: center; }
    .order-row .label { font-size: 14px; color: var(--color-slate-900); }
    .arrows { display: flex; gap: 2px; }
    .arrows button { padding: 2px 8px; cursor: pointer; border: 1px solid var(--color-slate-300);
                     background: var(--color-white); border-radius: var(--radius-sm); font-size: 12px; }
    .arrows button:hover { background: var(--color-slate-50); }
    .arrows button:disabled { opacity: 0.4; cursor: default; }
  `; }
    connectedCallback() {
        super.connectedCallback();
        this.seedOrders();
    }
    // Seed each ordering task with a stable shuffled order if the student has no answer yet.
    // Runs on connect (when tasks + draft are ready) and again before any update, so the
    // ordered answer is persisted even if the student never touches the arrows.
    willUpdate() {
        this.seedOrders();
    }
    seedOrders() {
        const d = draft.value;
        if (!d)
            return;
        let next = d.navigateAnswers;
        let changed = false;
        for (const t of this.tasks) {
            if (t.answer_type === "navigation_breadcrumb" && (t.options?.length ?? 0) > 0) {
                const cur = next[t.id];
                if (!cur || cur.length === 0) {
                    next = { ...next, [t.id]: seededOrder(t.id, t.options) };
                    changed = true;
                }
            }
        }
        if (changed)
            draft.value = { ...d, navigateAnswers: next };
    }
    setAns(tid, value) {
        const d = draft.value;
        draft.value = { ...d, navigateAnswers: { ...d.navigateAnswers, [tid]: value } };
    }
    toggleMulti(tid, opt, checked) {
        const d = draft.value;
        const cur = d.navigateAnswers[tid] ?? [];
        const nextSel = checked ? [...cur, opt] : cur.filter((x) => x !== opt);
        draft.value = { ...d, navigateAnswers: { ...d.navigateAnswers, [tid]: nextSel } };
    }
    moveOrder(tid, opt, delta) {
        const d = draft.value;
        const order = [...(d.navigateAnswers[tid] ?? [])];
        const i = order.indexOf(opt);
        const j = i + delta;
        if (i < 0 || j < 0 || j >= order.length)
            return;
        [order[i], order[j]] = [order[j], order[i]];
        draft.value = { ...d, navigateAnswers: { ...d.navigateAnswers, [tid]: order } };
    }
    render() {
        const ans = draft.value?.navigateAnswers ?? {};
        return html `
      ${this.tasks.map((t, i) => html `
        <div class="task">
          <div class="qnum">Question ${i + 1}</div>
          <div class="prompt">${t.prompt}</div>
          ${t.answer_type === "free_text"
            ? html `<textarea data-t=${t.id} .value=${ans[t.id] ?? ""}
                             @input=${(e) => this.setAns(t.id, e.target.value)}></textarea>`
            : t.answer_type === "single_select"
                ? html `<select data-t=${t.id} .value=${ans[t.id] ?? ""}
                            @change=${(e) => this.setAns(t.id, e.target.value)}>
                     <option value="">Select an option</option>
                     ${(t.options ?? []).map((o) => html `<option value=${o}>${o}</option>`)}
                   </select>`
                : t.answer_type === "multi_select"
                    ? html `${(t.options ?? []).map((o) => html `
                <label class="opt">
                  <input type="checkbox" data-t=${t.id} data-opt=${o}
                         .checked=${ans[t.id]?.includes(o) ?? false}
                         @change=${(e) => this.toggleMulti(t.id, o, e.target.checked)} />
                  ${o}
                </label>`)}`
                    : html `
              <p class="order-hint">Use the arrows to put these in order, first at the top.</p>
              ${(ans[t.id] ?? []).map((opt, idx, arr) => html `
                <div class="order-row">
                  <span class="seqno">${idx + 1}</span>
                  <span class="label">${opt}</span>
                  <span class="arrows">
                    <button data-up=${opt} aria-label="Move ${opt} up" ?disabled=${idx === 0}
                            @click=${() => this.moveOrder(t.id, opt, -1)}>▲</button>
                    <button data-down=${opt} aria-label="Move ${opt} down" ?disabled=${idx === arr.length - 1}
                            @click=${() => this.moveOrder(t.id, opt, 1)}>▼</button>
                  </span>
                </div>`)}`}
        </div>
      `)}
    `;
    }
};
__decorate([
    property({ attribute: false })
], ModeNavigate.prototype, "tasks", void 0);
ModeNavigate = __decorate([
    customElement("mode-navigate")
], ModeNavigate);
export { ModeNavigate };
